<template>
  <div class="rightbar">
    <div class="rightbar__inner" :class="{ 'p-0 ': !isUserAuth }">
      <BaseField
        v-if="isUserAuth"
        v-model:value="searchText"
        @input="fetchSearchResults"
        class="sign-in__field"
        :placeholder="'Search'"
        isSearch
        isBottomBorderOnFocus></BaseField>
      <div class="rightbar__posts" :class="{ 'mt-[26px]': !isUserAuth }">
        <div v-if="loading" class="pb-4">Loading...</div>
        <div v-else-if="searchText && noSearchResults" class="pb-4">
          <h5>No results found for "{{ noSearchResults }}"</h5>
        </div>
        <div
          v-else-if="!searchText && posts.length && !allUsers.length && !allPosts.length"
          class="rightbar__posts-list">
          <h4 class="rightbar__title text-left pl-3 pt-3 text-foregroundPrimary text-base mb-3 font-medium">
            Suggested for you
          </h4>
          <div v-for="el in posts" :key="el.id">
            <SuggestedPost :data="el" :type="'user'" isRightBar :gap="0" :isAuth="!!userData"></SuggestedPost>
          </div>
        </div>
        <div v-else-if="allUsers.length || allPosts.length">
          <div v-if="allUsers.length" class="rightbar__posts-list" @scroll="handleUsersScroll">
            <h4 class="rightbar__title text-left pl-3 pt-3 text-foregroundPrimary text-base mb-3 font-medium">
              People Results
            </h4>
            <div v-for="el in displayedUsers" :key="el.id">
              <SuggestedPost :data="el" :type="'user'" isRightBar :gap="0" :isAuth="!!userData"></SuggestedPost>
            </div>
            <div class="text-center mt-2" v-if="usersLoading">
              <ProgressSpinner
                style="width: 40px; height: 40px; margin: 0 auto"
                strokeWidth="5"
                fill="transparent"
                animationDuration="1.2s"
                aria-label="Loading users" />
            </div>
          </div>
          <div v-if="allUsers.length && allPosts.length" class="mb-[32px]"></div>
          <div v-if="allPosts.length" class="rightbar__posts-list" @scroll="handlePostsScroll">
            <h4 class="rightbar__title text-left pl-3 pt-3 text-foregroundPrimary text-base mb-3 font-medium">
              Post Results
            </h4>
            <div v-for="post in displayedPosts" :key="post.id">
              <CompactPost :post="post"></CompactPost>
            </div>
            <div class="text-center mt-2" v-if="postsLoading">
              <ProgressSpinner
                style="width: 40px; height: 40px; margin: 0 auto"
                strokeWidth="5"
                fill="transparent"
                animationDuration="1.2s"
                aria-label="Loading posts" />
            </div>
          </div>
        </div>
        <div v-else class="pb-4">
          <h5>No data.</h5>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';

import ProgressSpinner from 'primevue/progressspinner';
import { useStore } from 'vuex';

import CompactPost from '@/components/Post/CompactPost.vue';
import { debounce } from '@/utils/debounce.js';

import SuggestedPost from '../Post/SuggestedPost.vue';

const store = useStore();

const isUserAuth = computed(() => store.getters.getAuthStatus);

const searchText = computed({
  get: () => store.getters.getSearchText,
  set: value => store.dispatch('updateSearchText', value),
});
const userData = computed(() => store.getters.getAuthUser);
const loading = ref(false);

const getSuggestedPosts = () => store.dispatch('setSuggestedPosts');
const posts = computed(() => store.getters.getSuggestedPosts);

const displayedUsers = ref([]);
const displayedPosts = ref([]);
const usersOffset = ref(0);
const postsOffset = ref(0);
const pageSize = 10;
const usersLoading = ref(false);
const postsLoading = ref(false);
const allUsers = computed(() => store.getters.getSearchUsers);
const allPosts = computed(() => store.getters.getSearchPosts);
const noSearchResults = computed(() => store.getters.getNoSearchResults);

watch(searchText, newValue => {
  if (!newValue || !newValue.trim()) {
    store.commit('clearSearchResults');
    resetLists();
    getSuggestedPosts();
  } else {
    fetchSearchResults();
  }
});

const fetchSearchResults = debounce(async () => {
  loading.value = true;
  await store.dispatch('fetchSearchResults', searchText.value);
  resetLists();
  loading.value = false;
}, 700);

const resetLists = () => {
  displayedUsers.value = allUsers.value.slice(0, pageSize);
  displayedPosts.value = allPosts.value.slice(0, pageSize);
  usersOffset.value = pageSize;
  postsOffset.value = pageSize;
};

const loadMoreUsers = () => {
  if (usersOffset.value >= allUsers.value.length || usersLoading.value) return;

  usersLoading.value = true;
  setTimeout(() => {
    const nextUsers = allUsers.value.slice(usersOffset.value, usersOffset.value + pageSize);
    displayedUsers.value = [...displayedUsers.value, ...nextUsers];
    usersOffset.value += pageSize;
    usersLoading.value = false;
  }, 1000);
};

const loadMorePosts = () => {
  if (postsOffset.value >= allPosts.value.length || postsLoading.value) return;

  postsLoading.value = true;
  setTimeout(() => {
    const nextPosts = allPosts.value.slice(postsOffset.value, postsOffset.value + pageSize);
    displayedPosts.value = [...displayedPosts.value, ...nextPosts];
    postsOffset.value += pageSize;
    postsLoading.value = false;
  }, 1000);
};

const handlePostsScroll = e => {
  const element = e.target;
  if (element.scrollTop + element.clientHeight >= element.scrollHeight - 10) {
    loadMorePosts();
  }
};

const handleUsersScroll = e => {
  const element = e.target;
  if (element.scrollTop + element.clientHeight >= element.scrollHeight - 10) {
    loadMoreUsers();
  }
};

onMounted(async () => {
  if (userData) {
    loading.value = true;
    if (searchText.value) {
      fetchSearchResults();
    } else {
      await getSuggestedPosts();
    }
    loading.value = false;
  }
});
</script>

<style lang="scss">
.rightbar {
  max-width: 278px;
  text-align: center;
  font-size: 14px;
  &__inner {
    padding: 20px 0px;
    position: sticky;
    top: 80px;
    @include _1024 {
      padding: 20px 44px;
    }
    @include _767 {
      padding: 20px 32px;
    }
    @include _639 {
      padding: 20px 16px;
    }
  }
  &__posts {
    margin-top: 34px;
    border-radius: 10px;
    overflow: hidden;
    overflow-y: auto;
    max-height: calc(100vh - 195px);
    @include _1024 {
      max-height: unset;
    }

    &-list {
      overflow-y: auto;
      max-height: 450px;
      background: var(--post-bg);
      border-radius: 10px;
      padding: 0 10px;
      .grow {
        max-width: 120px;
        @include _1024 {
          max-width: 100%;
        }
      }
    }
    @include _1024 {
      max-width: 100%;
      margin: 20px auto 0;
    }
  }
  &__title {
    font-family: 'ClashDislay-Semibold';
    line-height: 150%;
  }
  @include _1024 {
    width: 100%;
    max-width: 100%;
  }
  @include _767 {
    position: relative;
    top: 0;
  }
}
</style>
